import { useCallback, useEffect, useState } from "react";
import { agentsApi } from "api/agents";
import { AvailableAgent } from "types";

interface UseAssignedSpecialistsOptions {
  agentId: string | undefined;
}

interface UseAssignedSpecialistsReturn {
  allSpecialists: AvailableAgent[];
  assignedAgents: AvailableAgent[];
  loading: boolean;
  error: string | null;
  isSaving: boolean;
  isDeleting: boolean;
  fetchData: () => Promise<void>;
  assignSpecialists: (specialistIds: string[]) => Promise<void>;
  removeSpecialist: (specialistId: string) => Promise<void>;
}

export const useAssignedSpecialists = ({
  agentId,
}: UseAssignedSpecialistsOptions): UseAssignedSpecialistsReturn => {
  const [allSpecialists, setAllSpecialists] = useState<AvailableAgent[]>([]);
  const [assignedAgents, setAssignedAgents] = useState<AvailableAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDataInternal = useCallback(
    async (signal?: AbortSignal) => {
      if (!agentId) return;

      setLoading(true);
      setError(null);
      try {
        // Fetch available agents and assigned agents in parallel
        const [availableAgentsResponse, assignedAgentsResponse] =
          await Promise.all([
            agentsApi.getAvailable(),
            agentsApi.getAssignedAgents(agentId),
          ]);

        // Check if request was aborted
        if (signal?.aborted) return;

        // Filter out current agent from available agents
        const availableAgents = availableAgentsResponse.items || [];
        const specialists = availableAgents.filter(
          (agent) => agent.id !== agentId
        );
        setAllSpecialists(specialists);

        // Convert AssignedAgent[] to AvailableAgent[] for compatibility
        const assigned: AvailableAgent[] = assignedAgentsResponse.map(
          (assignedAgent) => ({
            id: assignedAgent.id,
            name: assignedAgent.name,
            description: assignedAgent.description,
            status: assignedAgent.status,
            agent_type: assignedAgent.agent_type,
          })
        );
        setAssignedAgents(assigned);
      } catch (err: any) {
        if (signal?.aborted || err.name === "CanceledError") {
          return;
        }
        setError("agents.assignedSpecialists.failedToFetchSpecialists");
        console.error("Error fetching specialists:", err);
      } finally {
        setLoading(false);
      }
    },
    [agentId]
  );

  // Public fetchData function that doesn't require abort signal
  const fetchData = useCallback(async () => {
    await fetchDataInternal();
  }, [fetchDataInternal]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchDataInternal(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchDataInternal]);

  const assignSpecialists = useCallback(
    async (specialistIds: string[]) => {
      if (!agentId) return;

      setIsSaving(true);
      setError(null);
      try {
        // Assign new specialists to the orchestrator using the dedicated endpoint
        await agentsApi.assignAgents(agentId, { agent_ids: specialistIds });
        await fetchDataInternal();
      } catch (err) {
        setError("agents.assignedSpecialists.failedToAssignSpecialists");
        console.error("Error assigning specialists:", err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [agentId, fetchDataInternal]
  );

  const removeSpecialist = useCallback(
    async (specialistId: string) => {
      if (!agentId) return;

      setIsDeleting(true);
      setError(null);
      try {
        // Remove the specialist from the orchestrator using the dedicated endpoint
        await agentsApi.removeAssignedAgent(agentId, specialistId);
        await fetchDataInternal();
      } catch (err) {
        setError("agents.assignedSpecialists.failedToRemoveSpecialist");
        console.error("Error removing specialist:", err);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [agentId, fetchDataInternal]
  );

  return {
    allSpecialists,
    assignedAgents,
    loading,
    error,
    isSaving,
    isDeleting,
    fetchData,
    assignSpecialists,
    removeSpecialist,
  };
};
